import { useState, useRef, useEffect } from 'react';
import { Pencil } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import { useProfile } from '@/lib/providers';
import { AdminRole } from '@/types/custom';
import { usePageDescription } from '@/lib/hooks/use-page-description';

interface EditableDescriptionProps {
  pageKey: string;
  defaultText: string;
  className?: string;
}

export default function EditableDescription({
  pageKey,
  defaultText,
  className = '',
}: EditableDescriptionProps) {
  const { profile } = useProfile();
  const isAdmin = profile?.role === AdminRole;
  const { description, loading, saving, saveDescription } = usePageDescription(pageKey, defaultText);
  const { toast } = useToast();

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(description);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Keep draft in sync when description loads/changes
  useEffect(() => {
    if (!editing) {
      setDraft(description);
    }
  }, [description, editing]);

  // Focus and select textarea when entering edit mode
  useEffect(() => {
    if (editing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.select();
    }
  }, [editing]);

  const handleSave = async () => {
    const trimmed = draft.trim();
    if (trimmed === description) {
      setEditing(false);
      return;
    }

    try {
      await saveDescription(trimmed);
      toast({ title: 'Beschrijving opgeslagen' });
    } catch {
      toast({ title: 'Opslaan mislukt', variant: 'destructive' });
      setDraft(description);
    }
    setEditing(false);
  };

  const handleCancel = () => {
    setDraft(description);
    setEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    }
    if (e.key === 'Escape') {
      handleCancel();
    }
  };

  if (loading) {
    return <p className={`text-muted-foreground ${className}`}>{defaultText}</p>;
  }

  if (!isAdmin) {
    return <p className={`text-muted-foreground ${className}`}>{description}</p>;
  }

  if (editing) {
    return (
      <Textarea
        ref={textareaRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        disabled={saving}
        className={`text-muted-foreground resize-none ${className}`}
        rows={3}
      />
    );
  }

  return (
    <p
      className={`text-muted-foreground group/desc cursor-pointer inline-flex items-start gap-2 ${className}`}
      onClick={() => setEditing(true)}
    >
      <span>{description}</span>
      <Pencil className="h-4 w-4 shrink-0 mt-0.5 opacity-0 group-hover/desc:opacity-60 transition-opacity" />
    </p>
  );
}
